import type {
  Admin,
  Presentation,
  PresentationDetail,
  Slide,
  SlideConfig,
  SlideType,
  SlideTypeInfo,
} from "./types";

export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  // Written out rather than declared as constructor parameter properties,
  // which the template's `erasableSyntaxOnly` forbids.
  readonly status: number;
  readonly fields: FieldError[];

  constructor(status: number, message: string, fields: FieldError[] = []) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fields = fields;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    // The admin session is an httpOnly cookie, so every call must send it.
    credentials: "include",
    headers: init.body ? { "content-type": "application/json" } : undefined,
    ...init,
  });

  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const detail = body?.detail;
    // Config failures come back as [{field, message}]; everything else is a string.
    if (Array.isArray(detail)) {
      throw new ApiError(
        res.status,
        detail.map((d: FieldError) => d.message).join("; "),
        detail,
      );
    }
    throw new ApiError(res.status, typeof detail === "string" ? detail : res.statusText);
  }

  return body as T;
}

export const api = {
  login: (username: string, password: string) =>
    request<Admin>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),

  logout: () => request<void>("/api/auth/logout", { method: "POST" }),

  me: () => request<Admin>("/api/auth/me"),

  slideTypes: () => request<SlideTypeInfo[]>("/api/presentations/slide-types"),

  listPresentations: () => request<Presentation[]>("/api/presentations"),

  createPresentation: (title: string, seedDemo = false) =>
    request<PresentationDetail>("/api/presentations", {
      method: "POST",
      body: JSON.stringify({ title, seed_demo: seedDemo }),
    }),

  getPresentation: (id: number) => request<PresentationDetail>(`/api/presentations/${id}`),

  renamePresentation: (id: number, title: string) =>
    request<PresentationDetail>(`/api/presentations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),

  deletePresentation: (id: number) =>
    request<void>(`/api/presentations/${id}`, { method: "DELETE" }),

  addSlide: (presentationId: number, type: SlideType, position?: number) =>
    request<Slide>(`/api/presentations/${presentationId}/slides`, {
      method: "POST",
      body: JSON.stringify({ type, question: "", position }),
    }),

  reorderSlides: (presentationId: number, slideIds: number[]) =>
    request<PresentationDetail>(`/api/presentations/${presentationId}/slides/order`, {
      method: "PUT",
      body: JSON.stringify({ slide_ids: slideIds }),
    }),

  updateSlide: (
    slideId: number,
    patch: { question?: string; config?: SlideConfig },
  ) =>
    request<Slide>(`/api/slides/${slideId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deleteSlide: (slideId: number) => request<void>(`/api/slides/${slideId}`, { method: "DELETE" }),
};

// ---- Live sessions ----

export const live = {
  start: (presentationId: number) =>
    request<{ session: { id: number; code: string } }>(
      `/api/presentations/${presentationId}/sessions`,
      { method: "POST" },
    ),

  lookup: (code: string) => request<{ code: string; status: string }>(`/api/join/${code}`),

  join: (code: string, name: string, token?: string | null) =>
    request<{ token: string; participant_id: number; name: string; session_id: number }>(
      "/api/join",
      { method: "POST", body: JSON.stringify({ code, name, token }) },
    ),

  summary: (sessionId: number) => request<any>(`/api/sessions/${sessionId}/summary`),
};
